import { trans } from "@mongez/localization";
import { Form } from "@mongez/react-form";
import { useLocation, useRevalidator } from "@remix-run/react";
import { addressesAtom, useAddAddress } from "core/addresses";
import { checkoutAtom, useGuestSetShippingAddress } from "core/checkout";
import { createPortal } from "core/shared";
import { userAtom } from "core/user";
import { URLS } from "framework/config/urls";
import {
  errorToast,
  PhoneNumberInput,
  successToast,
  SwitchInput,
  TextareaInput,
  TextInput,
} from "~/design-system/components";
import { Button, SubmitButton } from "~/design-system/components/button";
import { Portal } from "~/modules/shared/components";
import { SelectAddressCityAndDistrict } from "../addresses/SelectAddressCityAndDistrict";

export const addAddressPortal = createPortal("add-address-portal");

export function AddAddressPortal() {
  const user = userAtom.useValue();

  const location = useLocation();

  const revalidator = useRevalidator();

  const { addAddressSubmit } = useAddAddress({
    onSuccess: (data: any) => {
      successToast(trans("addressAddedSuccessfully"));
      addressesAtom.update(data.addresses);
      checkoutAtom.change("addresses", data.addresses);
      revalidator.revalidate();
      addAddressPortal.close();
    },
    onError: (error: any) => {
      errorToast(error);
    },
  });

  const { setGuestShippingAddress } = useGuestSetShippingAddress({
    onSuccess: (data) => {
      successToast(trans("addressAddedSuccessfully"));
      const address = data.cart.shippingAddress;
      const { shippingType } = data.cart;

      if (shippingType === "pickup") {
        checkoutAtom.merge({
          addresses: [],
          cart: data.cart,
        });
      } else {
        checkoutAtom.merge({
          addresses: [address],
          cart: data.cart,
        });
      }
      revalidator.revalidate();
      addAddressPortal.close();
    },
    onError: (error) => {
      errorToast(error);
    },
  });

  const handleAddress =
    user.userType === "user" || !location.pathname.includes(URLS.checkout.root)
      ? addAddressSubmit
      : setGuestShippingAddress;

  return (
    <Portal portal={addAddressPortal} title={trans("addNewAddress")}>
      <Form onSubmit={handleAddress} className="flex flex-col gap-6">
        <TextInput
          classes={{
            input: "bg-white/50",
          }}
          name="name"
          placeholder={trans("name")}
          defaultValue={user.name}
          required
        />

        <PhoneNumberInput
          id="phoneNumber"
          name="phoneNumber"
          minLength={11}
          classes={{
            input: "bg-white/50",
          }}
          label={trans("phoneNumber")}
          defaultValue={user.phoneNumber}
          placeholder={trans("phoneNumber")}
          className="w-full"
          inputClassName="rounded-lg py-4 px-4"
          variant="filled"
          required
        />

        <TextareaInput
          classes={{
            input: "bg-white/50",
          }}
          name="address"
          placeholder={trans("address")}
          required
        />

        <SelectAddressCityAndDistrict />

        <TextInput
          classes={{
            input: "bg-white/50",
          }}
          name="label"
          placeholder={trans("addressLabelHint")}
        />

        <TextInput
          classes={{
            input: "bg-white/50",
          }}
          name="landmark"
          placeholder={trans("landmark")}
        />
        {user.userType === "user" && (
          <SwitchInput
            name="isPrimary"
            label={trans("makePrimary")}
            color="secondary"
          />
        )}
        <div className="mt-6 flex items-center gap-4">
          <Button
            onClick={addAddressPortal.close}
            className="flex-1 justify-center rounded-full border bg-[#DFDFDF] px-6 py-3 font-medium hover:bg-[#d3d3d3]"
          >
            {trans("cancel")}
          </Button>
          <SubmitButton className="bg-primary-light hover:bg-primary-300 flex-1 justify-center rounded-full border px-6 py-3 font-medium">
            {trans("create")}
          </SubmitButton>
        </div>
      </Form>
    </Portal>
  );
}
